import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft, Loader2, MapPin, Zap, Search, Send, Eye, MousePointerClick,
  CheckCircle2, XCircle, Upload, Ban, Mail, Phone, Globe, Star, Undo2,
} from "lucide-react";

/**
 * AdminOutreach — funnel dashboard + prospect directory + invite history for
 * the auto-invite system. Wired to the outreach.py backend routes.
 */

function TabButton({ active, onClick, label, count, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${active ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:text-slate-900 border border-slate-200"}`}
    >
      {label}
      {count != null && <span className={`ml-1.5 text-[10px] font-bold ${active ? "text-white/70" : "text-slate-400"}`}>{count}</span>}
    </button>
  );
}

function KpiCard({ label, value, sublabel, tone = "text-slate-900", testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      <div className={`font-display text-2xl font-bold ${tone}`}>{value}</div>
      {sublabel && <div className="text-[11px] text-slate-500 mt-0.5">{sublabel}</div>}
    </div>
  );
}

export default function AdminOutreach() {
  const nav = useNavigate();
  const [tab, setTab] = useState("funnel"); // funnel | prospects | invites
  const [funnel, setFunnel] = useState(null);
  const [prospects, setProspects] = useState([]);
  const [prospectSearch, setProspectSearch] = useState("");
  const [showSuppressed, setShowSuppressed] = useState(false);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [importBusy, setImportBusy] = useState(false);

  const loadFunnel = useCallback(() => {
    api.get("/admin/outreach/funnel?days=30").then((r) => setFunnel(r.data)).catch(() => {});
  }, []);
  const loadProspects = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (prospectSearch.trim()) params.set("q", prospectSearch.trim());
    if (showSuppressed) params.set("only_suppressed", "true");
    api.get(`/admin/outreach/prospects?${params.toString()}&limit=300`)
      .then((r) => setProspects(r.data.prospects))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [prospectSearch, showSuppressed]);
  const loadInvites = useCallback(() => {
    setLoading(true);
    api.get("/admin/outreach/invites?limit=300").then((r) => setInvites(r.data.invites)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadFunnel(); }, [loadFunnel]);
  useEffect(() => { if (tab === "prospects") loadProspects(); }, [tab, loadProspects]);
  useEffect(() => { if (tab === "invites") loadInvites(); }, [tab, loadInvites]);

  const suppress = async (p) => {
    if (!window.confirm(`Suppress ${p.business_name}? We'll never invite them again unless you unsuppress.`)) return;
    setBusyId(p.id);
    try {
      await api.post(`/admin/outreach/prospects/${p.id}/suppress`, { reason: "admin_manual" });
      toast.success("Suppressed");
      loadProspects();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data) || "Failed");
    } finally { setBusyId(null); }
  };
  const unsuppress = async (p) => {
    setBusyId(p.id);
    try {
      await api.post(`/admin/outreach/prospects/${p.id}/unsuppress`, {});
      toast.success("Un-suppressed");
      loadProspects();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data) || "Failed");
    } finally { setBusyId(null); }
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/outreach/prospects/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.inserted} new, ${data.updated} updated${data.errors.length ? ` · ${data.errors.length} errors` : ""}`);
      loadProspects();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data) || "Import failed");
    } finally {
      setImportBusy(false);
      e.target.value = "";
    }
  };

  const fmtPct = (v) => (v * 100).toFixed(1) + "%";

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-3">
          <Button data-testid="outreach-back-btn" variant="outline" size="sm" onClick={() => nav("/admin")} className="rounded-xl">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Admin
          </Button>
          <div className="flex-1">
            <div className="font-display font-bold text-slate-900">Outreach</div>
            <div className="text-xs text-slate-500">Auto-invite nearby screen companies to claim your unrouted leads.</div>
          </div>
          <label className="inline-flex items-center gap-1.5 rounded-xl bg-white border border-slate-300 hover:border-slate-500 text-sm font-medium px-3 h-9 cursor-pointer" data-testid="import-csv-label">
            {importBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Import CSV
            <input type="file" accept=".csv" onChange={onImport} className="hidden" disabled={importBusy} data-testid="import-csv-input" />
          </label>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6 space-y-5">
        <div className="flex gap-2 flex-wrap">
          <TabButton active={tab === "funnel"} onClick={() => setTab("funnel")} label="Funnel" testid="tab-funnel" />
          <TabButton active={tab === "prospects"} onClick={() => setTab("prospects")} label="Prospects" count={funnel?.prospects_total} testid="tab-prospects" />
          <TabButton active={tab === "invites"} onClick={() => setTab("invites")} label="Invite history" count={funnel?.invites_sent} testid="tab-invites" />
        </div>

        {tab === "funnel" && (
          <div className="space-y-5" data-testid="funnel-tab">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <KpiCard label="Prospects" value={funnel?.prospects_total ?? "–"} sublabel={`${funnel?.prospects_with_email ?? 0} with email · ${funnel?.prospects_suppressed ?? 0} suppressed`} testid="kpi-prospects" />
              <KpiCard label="Invites sent (30d)" value={funnel?.invites_sent ?? "–"} tone="text-slate-900" testid="kpi-sent" />
              <KpiCard label="Opened" value={funnel?.invites_opened ?? "–"} sublabel={funnel ? fmtPct(funnel.open_rate) + " open rate" : ""} tone="text-blue-600" testid="kpi-opened" />
              <KpiCard label="Signed up" value={funnel?.invites_claimed ?? "–"} sublabel={funnel ? fmtPct(funnel.claim_rate) + " signup rate" : ""} tone="text-emerald-600" testid="kpi-claimed" />
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="font-display font-bold text-slate-900 mb-3">How it works</div>
              <ol className="text-sm text-slate-700 space-y-2 list-decimal pl-5">
                <li>Unrouted leads land in <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">Admin Lead Hub → Orphan bucket</span>.</li>
                <li>You (or the auto-scheduler later) click <span className="font-semibold">&quot;Auto-invite 3 nearby&quot;</span> on the lead.</li>
                <li>We search Google Places for local screen repair companies, scrape their website for a contact email, and send a &quot;curious tone&quot; invite.</li>
                <li>Each invite has an open pixel + click tracker so this dashboard shows opens, clicks, and signups.</li>
                <li>60-day dedupe + 6/year cap protect your prospect list. If a company unsubscribes, they&apos;re suppressed forever.</li>
              </ol>
            </div>
          </div>
        )}

        {tab === "prospects" && (
          <div className="space-y-3" data-testid="prospects-tab">
            <div className="rounded-xl border border-slate-200 bg-white p-3 flex items-center gap-2">
              <div className="flex-1 min-w-[200px] relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  data-testid="prospect-search"
                  placeholder="Search name, email, phone, address…"
                  value={prospectSearch}
                  onChange={(e) => setProspectSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") loadProspects(); }}
                  className="h-9 rounded-lg pl-9"
                />
              </div>
              <Button size="sm" variant="outline" onClick={loadProspects} className="rounded-lg">Search</Button>
              <label className="inline-flex items-center gap-2 text-xs text-slate-700 ml-2">
                <input type="checkbox" checked={showSuppressed} onChange={(e) => setShowSuppressed(e.target.checked)} data-testid="show-suppressed" />
                Show suppressed
              </label>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              {loading ? (
                <div className="px-6 py-10 text-center text-slate-500 text-sm"><Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Loading…</div>
              ) : prospects.length === 0 ? (
                <div className="px-6 py-10 text-center text-slate-500 text-sm">
                  No prospects yet. Trigger &quot;Auto-invite&quot; on an orphan lead or import a CSV.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-[11px] font-bold uppercase tracking-widest text-slate-500">
                    <tr>
                      <th className="text-left px-4 py-2.5">Business</th>
                      <th className="text-left px-4 py-2.5">Contact</th>
                      <th className="text-left px-4 py-2.5">Rating</th>
                      <th className="text-left px-4 py-2.5">Invites</th>
                      <th className="text-right px-4 py-2.5">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prospects.map((p) => (
                      <tr key={p.id} data-testid={`prospect-row-${p.id}`} className={`border-t border-slate-100 ${p.is_suppressed ? "bg-slate-50 opacity-70" : "hover:bg-slate-50/70"}`}>
                        <td className="px-4 py-3">
                          <div className="font-semibold text-slate-900 flex items-center gap-1.5">
                            {p.is_suppressed && <Ban className="w-3.5 h-3.5 text-slate-400" title="Suppressed" />}
                            {p.is_wsq_tenant && <span className="rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5">WSQ tenant</span>}
                            {p.business_name}
                          </div>
                          <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2">
                            <MapPin className="w-3 h-3" /> {p.formatted_address || "—"}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-700 space-y-0.5">
                          {p.email ? <div className="flex items-center gap-1"><Mail className="w-3 h-3 text-slate-400" /> {p.email}</div> : <div className="text-slate-400 italic">no email</div>}
                          {p.phone ? <div className="flex items-center gap-1"><Phone className="w-3 h-3 text-slate-400" /> {p.phone}</div> : null}
                          {p.website ? <div className="flex items-center gap-1"><Globe className="w-3 h-3 text-slate-400" /> <a href={p.website} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline truncate max-w-[200px]">{p.website.replace(/^https?:\/\//, "")}</a></div> : null}
                        </td>
                        <td className="px-4 py-3 text-xs">
                          {p.rating ? (
                            <div className="flex items-center gap-1">
                              <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                              <span className="font-semibold text-slate-900">{p.rating}</span>
                              <span className="text-slate-500">({p.user_rating_count || 0})</span>
                            </div>
                          ) : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-700">
                          <div className="font-semibold">{p.invite_count || 0}</div>
                          {p.last_invited_at && <div className="text-[10px] text-slate-500">{new Date(p.last_invited_at).toLocaleDateString()}</div>}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end">
                            {p.is_suppressed ? (
                              <Button size="sm" variant="outline" onClick={() => unsuppress(p)} disabled={busyId === p.id} className="rounded-lg h-8 px-2.5 text-xs" data-testid={`unsuppress-${p.id}`}>
                                <Undo2 className="w-3 h-3 mr-1" /> Unsuppress
                              </Button>
                            ) : (
                              <Button size="sm" variant="outline" onClick={() => suppress(p)} disabled={busyId === p.id} className="rounded-lg h-8 px-2.5 text-xs text-rose-700 border-rose-200 hover:bg-rose-50" data-testid={`suppress-${p.id}`}>
                                <Ban className="w-3 h-3 mr-1" /> Suppress
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {tab === "invites" && (
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="invites-tab">
            {loading ? (
              <div className="px-6 py-10 text-center text-slate-500 text-sm"><Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Loading…</div>
            ) : invites.length === 0 ? (
              <div className="px-6 py-10 text-center text-slate-500 text-sm">No invites sent yet.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] font-bold uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-2.5">Sent</th>
                    <th className="text-left px-4 py-2.5">To</th>
                    <th className="text-left px-4 py-2.5">Lead</th>
                    <th className="text-left px-4 py-2.5">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {invites.map((inv) => {
                    const opened = !!inv.opened_at;
                    const clicked = !!inv.clicked_at;
                    const claimed = !!inv.claimed_at;
                    return (
                      <tr key={inv.id} data-testid={`invite-row-${inv.id}`} className="border-t border-slate-100 hover:bg-slate-50/70">
                        <td className="px-4 py-3 text-xs text-slate-700">{new Date(inv.sent_at).toLocaleString()}</td>
                        <td className="px-4 py-3">
                          <div className="font-semibold text-slate-900 text-sm truncate max-w-[220px]">{inv.prospect?.business_name || "—"}</div>
                          <div className="text-[11px] text-slate-500">{inv.to_email}</div>
                        </td>
                        <td className="px-4 py-3 text-xs">
                          <div className="text-slate-700">{inv.lead?.first_name} · {inv.lead?.zip_code}</div>
                          <div className="text-[11px] text-slate-500">${Number(inv.lead?.estimate_total || 0).toLocaleString()}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold text-slate-700">
                              <Send className="w-3 h-3" /> Sent
                            </span>
                            {opened && (
                              <span className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-700">
                                <Eye className="w-3 h-3" /> Opened
                              </span>
                            )}
                            {clicked && (
                              <span className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-700">
                                <MousePointerClick className="w-3 h-3" /> Clicked
                              </span>
                            )}
                            {claimed && (
                              <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                                <CheckCircle2 className="w-3 h-3" /> Signed up
                              </span>
                            )}
                            {inv.status === "failed" && (
                              <span className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[10px] font-semibold text-rose-700">
                                <XCircle className="w-3 h-3" /> Failed
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
